import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { NOME_DO_TIPO, ORDEM_DOS_TIPOS, vantagensDe } from '../data/vantagensDoCartao';
import { nomeDoCartao } from '../utils/nomeDoCartao';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import './VantagensDoCartao.css';

/**
 * "Vantagens deste cartão": o que a bandeira publica para o nível do cartão.
 *
 * Desenho: `docs/designs/vantagens-do-cartao.md`. A tabela e as regras de
 * onde vem cada linha estão em `data/vantagensDoCartao.ts`.
 *
 * ── O texto informa e manda conferir ─────────────────────────────────────
 *
 * "O que este cartão costuma oferecer", e nunca "você tem direito": a
 * bandeira muda as regras sem avisar, e o banco emissor pode oferecer menos.
 * Cada vantagem leva a CONDIÇÃO em destaque, porque é nela que o benefício se
 * perde, e o link da página oficial.
 */
interface Props {
  nome: string;
  bandeira?: string | null;
  nivel?: string | null;
  onClose: () => void;
}

const mesPorExtenso = (iso: string): string =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

const VantagensDoCartao: React.FC<Props> = ({ nome, bandeira, nivel, onClose }) => {
  const caixa = useRef<HTMLDivElement>(null);
  useTravarScroll();
  useFechavelComVoltar(true, onClose);
  useFocoNoModal(caixa);
  const vantagens = vantagensDe(bandeira, nivel);
  const cartao = nomeDoCartao(bandeira, nivel);
  const revisado = vantagens.map(v => v.revisadoEm).sort()[0];

  return createPortal(
    <div className="vdc-fundo" onClick={onClose}>
      <div
        className="vdc-caixa" role="dialog" aria-modal="true" aria-labelledby="vdc-titulo"
        ref={caixa} tabIndex={-1} onClick={e => e.stopPropagation()}
        onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
      >
        <div className="vdc-topo">
          <div>
            <h2 id="vdc-titulo">Vantagens deste cartão</h2>
            <p className="vdc-cartao">{nome}{cartao ? ` · ${cartao}` : ''}</p>
          </div>
          <button className="vdc-fechar" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className="vdc-corpo">
          {vantagens.length === 0 ? (
            <p className="vdc-nota">
              Ainda não temos as vantagens deste cartão na nossa lista. Você encontra tudo na página
              da bandeira ou no app do seu banco.
            </p>
          ) : (
            <>
              <p className="vdc-nota">
                O que este cartão costuma oferecer, segundo a página da bandeira. As regras são dela e
                podem mudar, e o seu banco pode oferecer menos: confira antes de contar com elas.
              </p>
              {!nivel && (
                <p className="vdc-nota">
                  O seu banco não informou o nível do cartão, então só aparece o que vale para
                  qualquer cartão desta bandeira.
                </p>
              )}
              {ORDEM_DOS_TIPOS.map(tipo => {
                const doTipo = vantagens.filter(v => v.tipo === tipo);
                if (doTipo.length === 0) return null;
                return (
                  <section key={tipo}>
                    <h3 className="vdc-tipo">{NOME_DO_TIPO[tipo]}</h3>
                    <ul className="vdc-lista">
                      {doTipo.map(v => (
                        <li key={v.id}>
                          <strong>{v.nome}</strong>
                          <span>{v.oQueE}</span>
                          <p className="vdc-condicao"><em>Para valer:</em> {v.condicao}</p>
                          <a href={v.fonte} target="_blank" rel="noopener noreferrer">Ver na página da bandeira ↗</a>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
              {revisado && <p className="vdc-rodape">Conferido em {mesPorExtenso(revisado)}.</p>}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default VantagensDoCartao;
